import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Layers3,
  Clock,
  Users,
  Timer,
  Donut,
  Sandwich,
  Hourglass,
} from "lucide-react";
import DashboardMetricCard from "../DashboardMetricCard";
import { producaoService } from "../../services/producaoService";
import { bordeiroService } from "../../services/bordeiroService";
import { useFilaEstatisticas } from "../../hooks/useFilaEstatisticas";

interface ResumoMontagensProps {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}

const ResumoMontagens: React.FC<ResumoMontagensProps> = ({
  dataInicio,
  dataFim,
  lojaId,
}) => {
  const filters = useMemo(
    () => ({
      dataInicio,
      dataFim,
      lojaId: lojaId || undefined,
    }),
    [dataInicio, dataFim, lojaId]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["montagem-resumo", filters],
    queryFn: () => producaoService.getResumoMontagem(filters),
  });
  const { data: bordeiroResumo, isLoading: isLoadingBordeiroResumo } = useQuery(
    {
      queryKey: ["bordeiro-resumo-dashboard", filters],
      queryFn: () => bordeiroService.getResumoDashboard(filters),
    }
  );

  const { data: filaStats } = useFilaEstatisticas(lojaId);

  const emMontagem = useMemo(() => {
    if (!filaStats) return 0;
    return (
      filaStats.filaSalgada.emMontagem +
      filaStats.filaDoce.emMontagem +
      (filaStats.filaSalaoSalgada?.emMontagem ?? 0) +
      (filaStats.filaSalaoDoce?.emMontagem ?? 0)
    );
  }, [filaStats]);

  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null || Number.isNaN(seconds)) return "00:00:00";
    const total = Math.max(0, Math.round(seconds));
    const hh = Math.floor(total / 3600);
    const mm = Math.floor((total % 3600) / 60);
    const ss = total % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };

  return (
    <div className="space-y-8">
      <section
        className="space-y-2"
        aria-labelledby="dashboard-resumo-montagens"
      >
        <h2
          id="dashboard-resumo-montagens"
          className="text-lg font-semibold text-neutral-800"
        >
          Resumo de Montagens
        </h2>
        <div className="xs:grid-cols-2 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
          <DashboardMetricCard
            accent="montagem"
            icon={<Layers3 aria-hidden />}
            label="Montagens Finalizadas"
            isLoading={isLoading}
            value={data?.totalProducoesFinalizadas ?? 0}
          />
          <DashboardMetricCard
            accent="montagem"
            icon={<Clock aria-hidden />}
            label="Tempo Médio"
            isLoading={isLoading}
            tabularValue
            value={data?.tempoMedio ?? "00:00:00"}
          />
          <DashboardMetricCard
            accent="montagem"
            icon={<Users aria-hidden />}
            label="Montadores"
            isLoading={isLoading}
            value={data?.quantidadeUsuarios ?? 0}
          />
          <DashboardMetricCard
            accent="montagem"
            icon={<Timer aria-hidden />}
            label="Em Montagem"
            value={emMontagem}
          />
        </div>
      </section>

      <section
        className="space-y-2"
        aria-labelledby="dashboard-resumo-bordeiro"
      >
        <h3
          id="dashboard-resumo-bordeiro"
          className="text-lg font-semibold text-neutral-800"
        >
          Resumo de Bordas e Paezinhos
        </h3>
        <div className="xs:grid-cols-2 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
          <DashboardMetricCard
            accent="bordeiro"
            icon={<Donut aria-hidden />}
            label="Total de Bordas"
            isLoading={isLoadingBordeiroResumo}
            value={bordeiroResumo?.bordas.totalProduzidas ?? 0}
          />
          <DashboardMetricCard
            accent="bordeiro"
            icon={<Hourglass aria-hidden />}
            label="Tempo médio"
            isLoading={isLoadingBordeiroResumo}
            tabularValue
            value={formatDuration(bordeiroResumo?.bordas.tempoMedioSegundos)}
          />
          <DashboardMetricCard
            accent="bordeiro"
            icon={<Sandwich aria-hidden />}
            label="Total de Pãezinhos"
            isLoading={isLoadingBordeiroResumo}
            value={bordeiroResumo?.paozinhos.totalProduzidos ?? 0}
          />
          <DashboardMetricCard
            accent="bordeiro"
            icon={<Clock aria-hidden />}
            label="Tempo médio"
            isLoading={isLoadingBordeiroResumo}
            tabularValue
            value={formatDuration(bordeiroResumo?.paozinhos.tempoMedioSegundos)}
          />
        </div>
      </section>
    </div>
  );
};

export default ResumoMontagens;
